import { createFileRoute } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { useCatalogExportType } from '~/features/exports/hooks/use-catalog-export-type';
import { useMainScrollReset } from '~/features/exports/hooks/use-main-scroll-reset';

// Explicit dynamic import — this feature's shared module graph (hooks/types/the profile form)
// is reachable from several sibling routes, which defeats the router plugin's automatic
// code-splitting heuristic; an explicit boundary keeps this page out of the main bundle
// regardless (see docs/catalog-export-ui.md's performance notes).
const ExportDetailPage = lazy(() =>
  import('~/features/exports/export-profiles/pages/export-detail-page').then((m) => ({
    default: m.ExportDetailPage,
  })),
);

/** One-off catalog export's own page (not a delivery run — see `../runs/$runId` for those).
 *  Ungated, like every route under `/exports/catalog` — open to every tenant user, matching
 *  the old (pre-redesign) `/exports/catalog` route, which had no `beforeLoad` at all. Cancel/
 *  archive/restore on THIS page are deliberately ungated too, not an oversight: they're
 *  export-service operations (`POST /v1/exports/{id}/cancel|archive|restore`), and export-service's
 *  own `ServiceWrite` dependency (`require_service_scopes`, `aes-delivery/apps/api/src/app/api/
 *  deps.py`) only scope-checks service-to-service callers — a human tenant user's token "pass[es]
 *  untouched", per that function's own doc comment. There is no server-side per-user permission on
 *  these endpoints to mirror here, unlike the delivery-PROFILE actions on `../runs/$runId`
 *  (`export-profiles/pages/delivery-run-page.tsx`), which integrations genuinely does gate on
 *  `deliveries:update` — `export-detail-page.tsx` previously (incorrectly) claimed the same was
 *  true here. */
export const Route = createFileRoute('/_authenticated/_tenant/exports/catalog/exports/$exportId')({
  component: RouteComponent,
});

function RouteComponent() {
  useCatalogExportType();
  useMainScrollReset();
  const { exportId } = Route.useParams();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <ExportDetailPage exportId={exportId} />
    </Suspense>
  );
}
